import React, { useCallback, useEffect, useState } from 'react'
import { Group, Loader, Center } from '@mantine/core'
import { IoAdd, IoTrash, IoPencil } from 'react-icons/io5'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import { AppButton, AppCard, AppText, AppModal, AppInput, AppTextarea, AppIconButton, AppStack, EmptyState, AdminTable } from '../../app/common/ui'

interface Platform {
  id: number
  name: string
  displayLabel: string
  aliases: string
  schematicId: string
  zoneLayoutId: string
  description: string
  createdAt: string
}

interface PlatformForm {
  name: string
  displayLabel: string
  aliases: string
  schematicId: string
  zoneLayoutId: string
  description: string
}

export function PlatformsAdminPanel () {
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<PlatformForm>({
    name: '',
    displayLabel: '',
    aliases: '',
    schematicId: '',
    zoneLayoutId: '',
    description: ''
  })
  const [submitting, setSubmitting] = useState(false)

  const loadPlatforms = useCallback(async () => {
    setLoading(true)
    try {
      const response = await agent.Admin.listPlatforms()
      setPlatforms(response)
    } catch {
      setPlatforms([])
      toast.error('Failed to load platforms')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadPlatforms()
  }, [loadPlatforms])

  const handleOpenModal = (platform?: Platform) => {
    if (platform) {
      setEditingId(platform.id)
      setForm({
        name: platform.name,
        displayLabel: platform.displayLabel,
        aliases: platform.aliases || '',
        schematicId: platform.schematicId,
        zoneLayoutId: platform.zoneLayoutId,
        description: platform.description || ''
      })
    } else {
      setEditingId(null)
      setForm({
        name: '',
        displayLabel: '',
        aliases: '',
        schematicId: '',
        zoneLayoutId: '',
        description: ''
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingId(null)
  }

  const handleSubmit = async () => {
    if (!form.name || !form.displayLabel || !form.schematicId) {
      toast.error('Name, display label, and schematic ID are required')
      return
    }

    setSubmitting(true)
    try {
      if (editingId) {
        await agent.Admin.updatePlatform(editingId, {
          displayLabel: form.displayLabel,
          aliases: form.aliases,
          schematicId: form.schematicId,
          zoneLayoutId: form.zoneLayoutId,
          description: form.description
        })
        toast.success('Platform updated')
      } else {
        await agent.Admin.createPlatform({
          name: form.name,
          displayLabel: form.displayLabel,
          aliases: form.aliases,
          schematicId: form.schematicId,
          zoneLayoutId: form.zoneLayoutId,
          description: form.description
        })
        toast.success('Platform created')
      }
      await loadPlatforms()
      handleCloseModal()
    } catch (err) {
      toast.error(editingId ? 'Failed to update platform' : 'Failed to create platform')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this platform? This cannot be undone.')) return

    try {
      await agent.Admin.deletePlatform(id)
      toast.success('Platform deleted')
      await loadPlatforms()
    } catch {
      toast.error('Failed to delete platform')
    }
  }

  if (loading) {
    return (
      <Center py={40}>
        <Loader size="sm" />
      </Center>
    )
  }

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'displayLabel', label: 'Display Label' },
    { key: 'schematicId', label: 'Schematic ID' },
    { key: 'aliases', label: 'Aliases' },
    { key: 'description', label: 'Description' }
  ]

  const rowActions = [
    { label: 'Edit', onClick: (platform: Platform) => handleOpenModal(platform) },
    { label: 'Delete', onClick: (platform: Platform) => handleDelete(platform.id), color: 'red' }
  ]

  return (
    <AppStack gap="lg">
      <Group position="apart">
        <AppText weight={600} size="lg">
          Firearm Platforms
        </AppText>
        <AppButton
          leftIcon={<IoAdd />}
          onClick={() => handleOpenModal()}
        >
          Add Platform
        </AppButton>
      </Group>

      {platforms.length === 0 ? (
        <EmptyState
          title="No platforms"
          message="No platforms found. Add one to get started."
        />
      ) : (
        <AdminTable
          columns={columns}
          data={platforms}
          pagination={{ totalCount: platforms.length, pageNumber: 1, pageSize: platforms.length }}
          onPageChange={() => {}}
          rowActions={rowActions}
        />
      )}

      <AppModal
        opened={showModal}
        onClose={handleCloseModal}
        title={editingId ? 'Edit Platform' : 'Add Platform'}
        size="md"
      >
        <AppStack gap="md">
          <AppInput
            label="Platform Name"
            placeholder="ar15"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
            disabled={!!editingId}
            description="Lowercase, no spaces (e.g., ar15, ak47, pistol)"
          />
          <AppInput
            label="Display Label"
            placeholder="AR-15"
            value={form.displayLabel}
            onChange={(e) => setForm({ ...form, displayLabel: e.currentTarget.value })}
            description="User-facing display name"
          />
          <AppInput
            label="Schematic ID"
            placeholder="ar15"
            value={form.schematicId}
            onChange={(e) => setForm({ ...form, schematicId: e.currentTarget.value })}
            description="Maps to SCHEMATIC_REGISTRY in client"
          />
          <AppInput
            label="Zone Layout ID"
            placeholder="ar15-default"
            value={form.zoneLayoutId}
            onChange={(e) => setForm({ ...form, zoneLayoutId: e.currentTarget.value })}
            description="Optional; defaults to schematic ID if blank"
          />
          <AppTextarea
            label="Aliases"
            placeholder="ar15|ar-15|ar 15|m4|m16"
            value={form.aliases}
            onChange={(e) => setForm({ ...form, aliases: e.currentTarget.value })}
            description="Pipe-delimited fuzzy match aliases"
          />
          <AppTextarea
            label="Description"
            placeholder="AR-15 / MSR rifles"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.currentTarget.value })}
            description="Optional; human-readable description"
          />
          <Group position="right" mt="md">
            <AppButton variant="subtle" onClick={handleCloseModal}>
              Cancel
            </AppButton>
            <AppButton
              onClick={handleSubmit}
              loading={submitting}
            >
              {editingId ? 'Update' : 'Create'}
            </AppButton>
          </Group>
        </AppStack>
      </AppModal>
    </AppStack>
  )
}
